import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import { AuthService } from './auth.service';
import { saveBlob } from '../utils/saveBlob';
import { parseContentDispositionFilename } from '../utils/contentDisposition';
import { StatementTable } from './financialStatement.service';

// W3-V FP&A: budget versions, variance, KPI pack, buyer profitability, board packs.

export type VersionKind = 'ORIGINAL' | 'REVISED' | 'FORECAST';
export type VersionStatus = 'DRAFT' | 'APPROVED' | 'LOCKED';

export interface BudgetVersion {
    id: string; institutionId: string; name: string; fiscalYearStart: string; kind: VersionKind;
    scenario?: string | null; status: VersionStatus; baseVersionId?: string | null; createdBy?: string | null;
    createdAt?: string; approvedBy?: string | null; approvedAt?: string | null; lockedAt?: string | null;
    notes?: string | null; createdByMe: boolean;
}
export interface VersionCreateRequest {
    name: string; fiscalYearStart: string; kind: 'ORIGINAL' | 'REVISED'; scenario?: string; notes?: string;
    mode: 'BLANK' | 'COPY_VERSION' | 'LAST_YEAR_ACTUALS'; sourceVersionId?: string;
    adjustPct?: number; adjustScope?: 'ALL' | 'REVENUE' | 'EXPENSE';
}
export interface BudgetLine { accountId: string; year: number; month: number; segmentId?: string | null; buyerOrderId?: string | null; departmentId?: string | null; projectId?: string | null; amount: number }
export interface XlsxImportResult { dryRun: boolean; rowsRead: number; linesAccepted: number; errors: string[]; lines: BudgetLine[] }

export interface VarianceComment { id: string; accountId: string; year: number; month: number; segmentId?: string | null; comment: string }
export interface VarianceRow {
    accountId: string; accountCode: string; accountName: string; accountType: string;
    budget: number; actual: number; variance: number; variancePct: number | null;
    flagged: boolean; unbudgeted: boolean; comments: VarianceComment[];
}
export interface VarianceReport {
    versionId: string; versionName: string; versionStatus: string; from: string; to: string;
    thresholdPct: number; minAmount: number; rows: VarianceRow[];
    totalBudgetRevenue: number; totalActualRevenue: number; totalBudgetExpense: number; totalActualExpense: number;
    budgetProfit: number; actualProfit: number; notes: string[];
}
export interface VarianceParams { versionId: string; from: string; to: string; segmentId?: string; buyerOrderId?: string; departmentId?: string; projectId?: string }

export interface Kpi { key: string; label: string; value: number | null; unit: string; howComputed: string; inputs: string[] }
export interface KpiResult { asOf: string; windowFrom: string; windowDays: number; kpis: Kpi[] }
export interface KpiSettings {
    varianceThresholdPct: number; varianceMinAmount: number; windowMonths: number;
    currentAssetPrefixes: string; currentLiabilityPrefixes: string; inventoryPrefixes: string; receivablePrefixes: string;
    payablePrefixes: string; revenuePrefixes: string; cogsPrefixes: string; interestPrefixes: string;
    taxPrefixes: string; debtPrefixes: string; defaults?: KpiSettings | null;
}

export interface BuyerProfitRow {
    buyerId: string | null; buyerName: string; orderCount: number; costedOrderCount: number;
    revenue: number; cost: number; margin: number; marginPct: number | null;
    planRevenue: number | null; planCost: number | null; planMargin: number | null; planMarginPct: number | null; uncostedRevenue?: number;
}
export interface BuyerProfitability {
    rows: BuyerProfitRow[]; totalRevenue: number; totalCost: number; totalMargin: number; totalMarginPct: number | null; notes: string[];
}

export interface PackSection { key: string; title?: string; commentary?: string }
export interface BoardPack { id: string; period: string; budgetVersionId?: string | null; status: 'DRAFT' | 'FINAL'; sections: PackSection[]; finalizedAt?: string | null }

const inst = () => AuthService.getSelectedInstitution()!;

const download = async (url: string, params: Record<string, string | undefined>, fallback: string) => {
    const response = await axios.get(url, { params, responseType: 'blob' });
    const disposition: string = response.headers?.['content-disposition'] ?? '';
    saveBlob(new Blob([response.data]), parseContentDispositionFilename(disposition, fallback));
};

export const FpaService = {
    listVersions: async (fiscalYearStart?: string): Promise<BudgetVersion[]> =>
        (await axios.get(ENDPOINTS.FPA.VERSIONS(inst()), { params: { fiscalYearStart } })).data,
    getVersion: async (id: string): Promise<BudgetVersion> => (await axios.get(ENDPOINTS.FPA.VERSION(inst(), id))).data,
    createVersion: async (req: VersionCreateRequest): Promise<BudgetVersion> => (await axios.post(ENDPOINTS.FPA.VERSIONS(inst()), req)).data,
    importLegacy: async (name: string, fiscalYearStart: string): Promise<BudgetVersion> =>
        (await axios.post(ENDPOINTS.FPA.IMPORT_LEGACY(inst()), { name, fiscalYearStart })).data,
    rollForward: async (baseVersionId: string, name: string, scenario?: string): Promise<BudgetVersion> =>
        (await axios.post(ENDPOINTS.FPA.ROLL_FORWARD(inst()), { baseVersionId, name, scenario })).data,
    updateVersion: async (id: string, body: { name: string; scenario?: string; notes?: string }): Promise<BudgetVersion> =>
        (await axios.put(ENDPOINTS.FPA.VERSION(inst(), id), body)).data,
    deleteVersion: async (id: string): Promise<void> => { await axios.delete(ENDPOINTS.FPA.VERSION(inst(), id)); },
    getLines: async (id: string): Promise<BudgetLine[]> => (await axios.get(ENDPOINTS.FPA.LINES(inst(), id))).data,
    /** windowFrom/windowTo = first and last month the grid showed; only stored lines inside it are replaced. */
    saveLines: async (id: string, lines: BudgetLine[], windowFrom?: string, windowTo?: string): Promise<BudgetLine[]> =>
        (await axios.put(ENDPOINTS.FPA.LINES(inst(), id), { lines, windowFrom, windowTo })).data,
    approve: async (id: string): Promise<BudgetVersion> => (await axios.post(ENDPOINTS.FPA.APPROVE(inst(), id))).data,
    /** Polish 4: switch a DRAFT between an original budget and a revision. */
    changeKind: async (id: string, kind: 'ORIGINAL' | 'REVISED'): Promise<BudgetVersion> =>
        (await axios.put(ENDPOINTS.FPA.KIND(inst(), id), { kind })).data,
    lock: async (id: string): Promise<BudgetVersion> => (await axios.post(ENDPOINTS.FPA.LOCK(inst(), id))).data,
    reopen: async (id: string): Promise<BudgetVersion> => (await axios.post(ENDPOINTS.FPA.REOPEN(inst(), id))).data,
    downloadTemplate: (id: string) => download(ENDPOINTS.FPA.TEMPLATE(inst(), id), {}, 'budget-template.xlsx'),
    importXlsx: async (id: string, file: File, dryRun: boolean): Promise<XlsxImportResult> => {
        const form = new FormData();
        form.append('file', file);
        return (await axios.post(ENDPOINTS.FPA.IMPORT(inst(), id), form, { params: { dryRun } })).data;
    },

    getVariance: async (p: VarianceParams): Promise<VarianceReport> =>
        (await axios.get(ENDPOINTS.FPA.VARIANCE(inst()), { params: p })).data,
    downloadVariance: (p: VarianceParams, format: 'pdf' | 'xlsx') =>
        download(ENDPOINTS.FPA.VARIANCE_EXPORT(inst()), { ...p, format }, `budget-variance-${p.to}.${format}`),
    saveComment: async (body: { versionId: string; accountId: string; year: number; month: number; segmentId?: string; comment: string }): Promise<VarianceComment | null> =>
        (await axios.put(ENDPOINTS.FPA.VARIANCE_COMMENTS(inst()), body)).data || null,

    getKpis: async (asOf?: string, segmentId?: string): Promise<KpiResult> =>
        (await axios.get(ENDPOINTS.FPA.KPIS(inst()), { params: { asOf, segmentId } })).data,
    getKpiSettings: async (): Promise<KpiSettings> => (await axios.get(ENDPOINTS.FPA.KPI_SETTINGS(inst()))).data,
    saveKpiSettings: async (body: Omit<KpiSettings, 'defaults'>): Promise<KpiSettings> =>
        (await axios.put(ENDPOINTS.FPA.KPI_SETTINGS(inst()), body)).data,

    getBuyerProfitability: async (shipFrom?: string, shipTo?: string): Promise<BuyerProfitability> =>
        (await axios.get(ENDPOINTS.FPA.BUYER_PROFITABILITY(inst()), { params: { shipFrom, shipTo } })).data,

    listBoardPacks: async (): Promise<BoardPack[]> => (await axios.get(ENDPOINTS.FPA.BOARD_PACKS(inst()))).data,
    getBoardPack: async (id: string): Promise<BoardPack> => (await axios.get(ENDPOINTS.FPA.BOARD_PACK(inst(), id))).data,
    createBoardPack: async (period: string, budgetVersionId?: string): Promise<BoardPack> =>
        (await axios.post(ENDPOINTS.FPA.BOARD_PACKS(inst()), { period, budgetVersionId })).data,
    saveBoardPack: async (id: string, budgetVersionId: string | undefined, sections: PackSection[]): Promise<BoardPack> =>
        (await axios.put(ENDPOINTS.FPA.BOARD_PACK(inst(), id), { budgetVersionId, sections })).data,
    finalizeBoardPack: async (id: string): Promise<BoardPack> => (await axios.post(ENDPOINTS.FPA.BOARD_PACK_FINALIZE(inst(), id))).data,
    deleteBoardPack: async (id: string): Promise<void> => { await axios.delete(ENDPOINTS.FPA.BOARD_PACK(inst(), id)); },
    getBoardPackTable: async (id: string): Promise<StatementTable> => (await axios.get(ENDPOINTS.FPA.BOARD_PACK_TABLE(inst(), id))).data,
    downloadBoardPackPdf: (id: string, period: string) => download(ENDPOINTS.FPA.BOARD_PACK_PDF(inst(), id), {}, `board-pack-${period}.pdf`),
};
